import React from 'react';
import { ClerkProvider } from '@clerk/react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { AppRouter } from './AppRouter';
import { ThemeProvider } from './components/common/ThemeContext';
import { ToastProvider } from './components/common/ToastContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { PostHogLogger } from './components/common/PostHogLogger';
import { ClerkAuthBridge } from './components/common/ClerkAuthBridge';
import { AuthPendingShell } from './components/common/AuthPendingShell';
import { SignOutProvider } from './contexts/SignOutContext';
import { logger } from '@repo/shared-utils';
import { RpcApiError, RPC_ERROR_CODE } from './services/api/transport';
import { clerkAppearance } from './config/clerkAppearance';

const clerkPublishableKey = (() => {
  if (typeof import.meta === 'undefined') return '';
  const v = (import.meta as { env?: Record<string, string> }).env?.VITE_CLERK_PUBLISHABLE_KEY;
  return typeof v === 'string' ? v.trim() : '';
})();

// Create QueryClient with global error handling
const createQueryClient = () => {
  // Create QueryCache with global error handler
  const queryCache = new QueryCache({
    onError: (error, query) => {
      const queryKey = query.queryKey ? JSON.stringify(query.queryKey) : 'unknown';

      if (error instanceof RpcApiError && error.code === RPC_ERROR_CODE.TRUST_ACCOUNT_MISSING) {
        logger.warn('[React Query] Trust bank account required for this action', {
          queryKey,
          code: error.code,
        });
        return;
      }

      const suppressForbidden =
        (query.meta as { suppressErrorLogOnForbidden?: boolean } | undefined)?.suppressErrorLogOnForbidden === true;
      if (suppressForbidden && error instanceof RpcApiError && error.statusCode === 403) {
        return;
      }

      logger.error('[React Query] Query error:', error, {
        queryKey,
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        queryHash: query.queryHash || 'unknown',
      });
    },
  });

  // Create MutationCache with global error handler
  const mutationCache = new MutationCache({
    onError: (error, variables, context, mutation) => {
      const mutationKey = mutation.options.mutationKey
        ? JSON.stringify(mutation.options.mutationKey)
        : 'unknown';

      if (error instanceof RpcApiError && error.code === RPC_ERROR_CODE.TRUST_ACCOUNT_MISSING) {
        logger.warn('[React Query] Trust bank account required for this mutation', {
          mutationKey,
          code: error.code,
        });
        return;
      }

      logger.error('[React Query] Mutation error:', error, {
        mutationKey,
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        variables: typeof variables === 'object' && variables !== null
          ? Object.keys(variables).join(', ')
          : String(variables).substring(0, 100), // First 100 chars or keys
        mutationId: mutation.mutationId || 'unknown',
      });
    },
  });

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 1,
      },
    },
  });
};

const queryClient = createQueryClient();

/** True when the app is served from a Vercel host (e.g. landing). Disable Vercel Analytics/Speed Insights on Railway (Live Demo) to avoid /_vercel/* requests and CSP errors. */
function isVercelHost(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.endsWith('vercel.app');
}

function App() {
  if (!clerkPublishableKey) {
    return (
      <ErrorBoundary>
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center dark:bg-slate-950">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Clerk is required</h1>
          <p className="max-w-md text-slate-600 dark:text-slate-400">
            Set <code className="rounded bg-slate-200 px-1.5 py-0.5 text-sm dark:bg-slate-800">VITE_CLERK_PUBLISHABLE_KEY</code> in the
            project root <code className="rounded bg-slate-200 px-1.5 py-0.5 text-sm dark:bg-slate-800">.env</code> and restart the dev server.
            Production builds fail without this variable.
          </p>
        </div>
      </ErrorBoundary>
    );
  }

  const inner = (
    <SignOutProvider>
      <PostHogLogger />
      <AuthPendingShell>
        <AppRouter />
      </AuthPendingShell>
      {isVercelHost() && (
        <>
          <Analytics />
          <SpeedInsights />
        </>
      )}
    </SignOutProvider>
  );

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ThemeProvider>
            <ClerkProvider
              publishableKey={clerkPublishableKey}
              appearance={clerkAppearance}
              allowedRedirectOrigins={typeof window === 'undefined' ? [] : [window.location.origin]}
            >
              <ClerkAuthBridge>{inner}</ClerkAuthBridge>
            </ClerkProvider>
          </ThemeProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
